// src/utils/audit.js
// Heuristic audit — fast, local, no backend needed.

// rewrittenDescription is saved as "" on import — treat empty string as missing
const getDescriptionHtml = (p) => {
  const rw = p?.rewrittenDescription;
  if (rw && typeof rw === 'string' && rw.trim()) return rw;
  const od = p?.originalDescription;
  if (od && typeof od === 'string' && od.trim()) return od;
  const dh = p?.descriptionHtml;
  if (dh && typeof dh === 'string' && dh.trim()) return dh;
  return '';
};

const getDescriptionText = (p) => {
  const html = getDescriptionHtml(p);
  if (html) return html.replace(/<[^>]+>/g, ' ');
  return String(p?.descriptionText || p?.description || '');
};

/**
 * UPDATED: Flexible Bullet Detection
 * Now catches standard <li> tags OR any line starting with common manual symbols.
 * Patterns: *, -, •, ◦, o, ~, or + followed by a space.
 */
const hasBullets = (txOrHtml = '') => /(<li\b|^\s*[-*•◦o~+]\s+)/im.test(txOrHtml);

const hasCTA = (tx = '') => /(buy now|download|add to cart|grab|try it free|learn more|save|bundle)/i.test(tx);
const includesGrades = (tx = '') => /(pre[-\s]?k|k[-\s]?12|k[-\s]?2|grade\s?\d+|\b\d{1,2}(st|nd|rd|th)\b)/i.test(tx);
const includesSubject = (tx = '') => /(math|ela|reading|phonics|science|history|social studies|art|music)/i.test(tx);
const includesStandards = (tx = '') => /(standards|common core|teks|ngss)/i.test(tx);

const extractAnchors = (html = '') => {
  const links = [];
  const re = /<a\s[^>]*href\s*=\s*(['"])(.*?)\1/gi;
  let m;
  while ((m = re.exec(html))) {
    const url = m[2]?.trim();
    if (!url || url.startsWith('#') || url.startsWith('mailto:')) continue;
    links.push(url);
  }
  return links;
};

const extractPlainUrls = (s = '') => {
  const urls = [];
  const re = /(https?:\/\/[^\s<>"')]+)\b/gi;
  let m;
  while ((m = re.exec(s))) urls.push(m[1]);
  return urls;
};

const unique = (arr) => Array.from(new Set(arr.filter(Boolean)));

function countBacklinks(product, tx, html) {
  const anchors = extractAnchors(html);
  const rawInHtml = extractPlainUrls(html);
  const rawInText = extractPlainUrls(tx);
  const fromMeta = Array.isArray(product?.backlinks) && product.backlinks.length ? product.backlinks : [];
  const all = unique([...anchors, ...rawInHtml, ...rawInText, ...fromMeta]);
  return all.length;
}

export function runAudit(product, overrides = {}) {
  const title = String(product?.productTitle || product?.title || '');
  const html = getDescriptionHtml(product);
  const tx = getDescriptionText(product);
  const words = tx.trim().split(/\s+/).filter(Boolean);
  const wordCount = words.length;

  const sentences = tx.split(/[.!?]+/).map((s) => s.trim()).filter(Boolean);
  const avgSentenceLength = sentences.length
    ? Math.round((wordCount / sentences.length) * 10) / 10
    : wordCount;

  const backlinksCount = countBacklinks(product, tx, html);

  // Define all checks
  const allChecks = [
    {
      key: 'title_length',
      label: 'Title length 45–80 chars',
      pass: title.length >= 45 && title.length <= 80,
      hint: `Current length: ${title.length}. Aim for 45–80 characters.`,
      weight: 10,
      guide: ['Longer titles catch more search traffic.', 'Keep critical keywords in the first 60 characters.'],
    },
    {
      key: 'title_keywords',
      label: 'Title mentions grade or subject',
      pass: includesGrades(title) || includesSubject(title),
      hint: 'Add a grade (e.g., “3rd Grade”) or subject (e.g., “Phonics”).',
      weight: 8,
      guide: ['Searchers often include their grade level in queries.', 'Explicitly naming the subject helps relevance scores.'],
    },
    {
      key: 'desc_length',
      label: 'Description length 250+ words',
      pass: wordCount >= 250,
      hint: `Current ~${wordCount} words. Add details, what’s included, and usage notes.`,
      weight: 12,
      guide: ['Deep descriptions help SEO indexing.', 'Use this space to answer FAQ before buyers ask.'],
    },
    {
      key: 'bullets',
      label: 'Uses bullet points for contents/what’s included',
      // Also check raw descriptionText field — covers cases where html resolves empty
      // but the pre-stripped text was saved separately on import.
      pass: hasBullets(html) || hasBullets(tx) || hasBullets(String(product?.descriptionText || '')),
      hint: 'Add a bulleted list of what’s included or key features.',
      weight: 10,
      guide: ['Bullets make listings scannable on mobile.', 'Highlight exactly what files are in the download.'],
    },
    {
      key: 'readability',
      label: 'Readable (avg sentence ≤ 20 words)',
      pass: avgSentenceLength <= 20,
      hint: `Current average sentence length ~${avgSentenceLength}. Split long sentences.`,
      weight: 10,
      guide: ['Teachers are busy—short sentences get read.', 'Avoid academic jargon where simple words work.'],
    },
    {
      key: 'cta',
      label: 'Clear call-to-action in closing',
      pass: hasCTA(tx),
      hint: 'Add a closing like “Grab this resource today” or “Add to Cart.”',
      weight: 8,
      guide: ['Explicit instructions increase conversion rate.', 'Remind users to follow your store for updates.'],
    },
    {
      key: 'backlinks',
      label: 'Includes cross-sell/backlinks to other products',
      pass: backlinksCount >= 2,
      hint: `Found ${backlinksCount} link${backlinksCount === 1 ? '' : 's'}. Link 2–3 related products/bundles.`,
      weight: 8,
      guide: ['Backlinks keep buyers in your store longer.', 'Link to the bundle this product is included in.'],
    },
    {
      key: 'standards',
      label: 'Mentions standards alignment',
      pass: includesStandards(tx) || (Array.isArray(product?.standards) && product.standards.length > 0),
      hint: 'Add Common Core/TEKS/NGSS or relevant standards.',
      weight: 8,
      guide: ['Administrators often require standards-aligned materials.', 'List specific CCSS or state codes.'],
    },
    {
      key: 'metadata',
      label: 'Has Grade or Subject Tags',
      // Check both raw Firestore fields (scrapedGrades/scrapedSubjects) AND the Hub-normalised
      // fields (_gradeRefs, _subjectAreaTags, _specificSubjectTags) — the ProductImprovementHub
      // renames these during normalisation so the raw fields may not be present on the product
      // object that reaches runAudit via the Hub.
      pass: (Array.isArray(product?.scrapedGrades) && product.scrapedGrades.length > 0) ||
            (Array.isArray(product?.scrapedSubjects) && product.scrapedSubjects.length > 0) ||
            (Array.isArray(product?._gradeRefs) && product._gradeRefs.length > 0) ||
            (Array.isArray(product?.gradeTags) && product.gradeTags.length > 0) ||
            (Array.isArray(product?._subjectAreaTags) && product._subjectAreaTags.length > 0) ||
            (Array.isArray(product?._specificSubjectTags) && product._specificSubjectTags.length > 0) ||
            (Array.isArray(product?.subjectTags) && product.subjectTags.length > 0),

      hint: 'Defined as "Not Grade Specific" or no subject on TPT? Consider adding these to your listing tags. If not required, click "Not relevant".',

      weight: 6,

      guide: [
        `Grades found: ${
          [...(product?.scrapedGrades || []), ...(product?._gradeRefs || []), ...(product?.gradeTags || [])].length > 0
            ? [...new Set([...(product?.scrapedGrades || []), ...(product?._gradeRefs || []), ...(product?.gradeTags || [])])].join(', ')
            : 'None detected'
        }`,
        `Subjects found: ${
          [...(product?.scrapedSubjects || []), ...(product?._subjectAreaTags || []), ...(product?._specificSubjectTags || []), ...(product?.subjectTags || [])].length > 0
            ? [...new Set([...(product?.scrapedSubjects || []), ...(product?._subjectAreaTags || []), ...(product?._specificSubjectTags || []), ...(product?.subjectTags || [])])].join(', ')
            : 'None detected'
        }`,
        'Structured tags (TPT product tags) help buyers find you using sidebar filters.',
        'If you recently added these on TPT, use the "Refresh Tags" button in the Hub.'
      ],
    },
  ];

  // Apply overrides to items for UI display (Green check for "Not relevant")
  const checksWithOverridesApplied = allChecks.map(check => ({
    ...check,
    pass: overrides?.[check.key] ? true : check.pass
  }));

  // Exclude overridden checks from the scoring math entirely
  const activeChecks = allChecks.filter(check => !overrides?.[check.key]);

  const totalWeight = activeChecks.reduce((s, c) => s + c.weight, 0) || 1;
  const passedWeight = activeChecks.filter((c) => c.pass).reduce((s, c) => s + c.weight, 0);

  const score = Math.round((passedWeight / totalWeight) * 100);

  const summary =
    score >= 85
      ? 'Excellent! A few tweaks could get you to perfect.'
      : score >= 70
      ? 'Solid foundation. Apply a couple of quick wins.'
      : 'Lots of easy wins available. Start with bullets and a stronger intro.';

  return {
    score,
    summary,
    items: checksWithOverridesApplied,
    breakdown: {
      wordCount,
      avgSentenceLength,
    },
    lastAuditedAt: new Date(),
  };
}